import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { auth } from '../api';
import { sessionManager, isMobileBrowser } from '../utils/auth';

const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const isMobile = isMobileBrowser();
        const hasToken = sessionManager.hasToken();
        
        console.log('🔐 Protected Route Check:');
        console.log('  - Device:', isMobile ? 'Mobile' : 'Desktop');
        console.log('  - Has token:', hasToken);
        console.log('  - Attempt:', attempts + 1);
        
        if (!hasToken && attempts > 0) {
          console.log('❌ No session token found after retry');
          setIsAuthenticated(false);
          return;
        }
        
        console.log('🔄 Checking authentication with backend...');
        const response = await auth.me();
        console.log('✅ Auth check successful:', response.data.email);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('❌ Auth check failed:', error.message);
        
        // On mobile, retry more times with longer delays
        const maxAttempts = isMobileBrowser() ? 4 : 3;
        const baseDelay = isMobileBrowser() ? 500 : 300;
        
        if (attempts < maxAttempts) {
          const delay = baseDelay * (attempts + 1);
          console.log(`⏳ Retrying auth check in ${delay}ms... (${attempts + 1}/${maxAttempts})`);
          setTimeout(() => {
            setAttempts(attempts + 1);
          }, delay);
        } else {
          console.log('❌ Max auth attempts reached');
          console.log('🗑️ Clearing session and redirecting to login');
          sessionManager.clearToken();
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();
  }, [attempts]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-red-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Authenticating...</p>
          {attempts > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Attempt {attempts + 1} of {isMobileBrowser() ? 4 : 3}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log('🚫 Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('✅ User authenticated, rendering protected content');
  return React.cloneElement(children, { user });
};

export default ProtectedRoute;
